import { Box, Typography } from "@mui/material";

import strings from "global/constants/StringConstants";

import {
  sendingLoaderDescription,
  sendingLoaderDocLine,
  sendingLoaderDot,
  sendingLoaderStyles as styles,
} from "./CustomSendingLoader.styles";
import { sendingLoaderTokens as tokens } from "./CustomSendingLoader.tokens";

/**
 * Which animation the card plays. `envelope` is the sender's "we're mailing
 * this out" story; `signature` is the signer's "we're applying your mark" one.
 */
type SendingLoaderVariant = "envelope" | "signature";

interface CustomSendingLoaderProps {
  /** Renders the overlay while the request is in flight. */
  isLoading?: boolean;
  /** Which animation to play. Defaults to the envelope. */
  variant?: SendingLoaderVariant;
  /** Overrides the headline — e.g. "Scheduling your request" for a delayed send. */
  title?: string;
  /** Overrides the copy under the headline. */
  description?: string;
}

/** Placeholder body lines on the document that slides into the envelope. */
const docLines: { width: string; strong: boolean }[] = [
  { width: "70%", strong: true },
  { width: "90%", strong: false },
  { width: "80%", strong: false },
];

/** Document drops into the envelope, the flap folds shut, the seal presses. */
const EnvelopeAnimation = () => (
  <Box sx={styles.envelopeStage} aria-hidden="true">
    <Box sx={styles.envelopeDoc}>
      {docLines.map((line, index) => (
        <Box
          key={line.width}
          sx={sendingLoaderDocLine(line.width, line.strong, index === 0)}
        />
      ))}
      <Box sx={styles.envelopeDocSignature} />
    </Box>

    <Box sx={styles.envelopeFront}>
      <Box sx={styles.envelopeBack} />
      <Box sx={styles.envelopePocket} />
      <Box sx={styles.envelopeFlap}>
        <Box sx={styles.envelopeFlapFace} />
      </Box>
      <Box sx={styles.envelopeSeal}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke={tokens.envelope.seal.iconColor}
          strokeWidth={tokens.envelope.seal.iconStrokeWidth}
          width={tokens.envelope.seal.iconSize}
          height={tokens.envelope.seal.iconSize}
        >
          <path d="M4.5 12.5l5 5L20 6.5" />
        </svg>
      </Box>
    </Box>
  </Box>
);

/** A signature is drawn onto its rule, then wipes away and repeats. */
const SignatureAnimation = () => (
  <Box sx={styles.signatureStage} aria-hidden="true">
    <svg
      viewBox={tokens.signature.viewBox}
      width={tokens.signature.svgWidth}
      height={tokens.signature.svgHeight}
      overflow="visible"
    >
      <path
        className={tokens.signature.pathClass}
        d={tokens.signature.strokePath}
        fill="none"
        stroke={tokens.signature.strokeColor}
        strokeWidth={tokens.signature.strokeWidth}
        strokeLinecap="round"
      />
      <path
        d={tokens.signature.baselinePath}
        stroke={tokens.signature.baselineColor}
        strokeWidth={tokens.signature.baselineWidth}
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

/**
 * Full-screen blocking loader for the two long-running steps of the signing
 * lifecycle: sending a request, and applying a signature.
 *
 * Unlike `CustomLoader` (a bare spinner used for every routine fetch) this one
 * explains what the wait is for and how long it may take, because both steps
 * fan out to documents, emails and an audit trail and can run for several
 * seconds. The animation loops until the caller flips `isLoading` back off.
 */
const CustomSendingLoader = (props: CustomSendingLoaderProps) => {
  if (!props.isLoading) return null;

  const variant = props.variant ?? "envelope";

  return (
    <Box sx={styles.overlay} role="status" aria-live="polite">
      <Box sx={styles.card}>
        <Box sx={styles.progressTrack}>
          <Box sx={styles.progressBar} />
        </Box>

        <Box sx={styles.body}>
          {variant === "signature" ? (
            <SignatureAnimation />
          ) : (
            <EnvelopeAnimation />
          )}

          <Typography component="div" sx={styles.title}>
            {props.title ?? strings.SENDING_REQUEST_TITLE}
            {tokens.title.dotDelays.map((delay, index) => (
              <Box component="span" key={delay} sx={sendingLoaderDot(index)}>
                .
              </Box>
            ))}
          </Typography>

          <Typography sx={sendingLoaderDescription(variant)}>
            {props.description ?? strings.SENDING_REQUEST_DESCRIPTION}
          </Typography>
        </Box>

        <Box sx={styles.footer}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke={tokens.footer.iconColor}
            strokeWidth={tokens.footer.iconStrokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            width={tokens.footer.iconSize}
            height={tokens.footer.iconSize}
            style={{ flexShrink: 0 }}
            aria-hidden="true"
          >
            <rect x="4" y="11" width="16" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          <Typography sx={styles.footerText}>
            {strings.SENDING_REQUEST_FOOTER}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default CustomSendingLoader;
